import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "../common/primitives.js";
const SIGNATURE_LEVELS = ["simple", "qualified"];
const SignatureLevelSchema = z.enum(SIGNATURE_LEVELS);
const SIGNATURE_REQUEST_STATUSES = [
  "pending",
  "completed",
  "declined",
  "cancelled",
  "expired"
];
const SignatureRequestStatusSchema = z.enum(SIGNATURE_REQUEST_STATUSES);
const SIGNER_STATUSES = ["pending", "signed", "declined"];
const SignerStatusSchema = z.enum(SIGNER_STATUSES);
const EVIDENCE_STATUSES = ["none", "pending", "ready", "failed"];
const SIGNATURE_CODE_CHANNELS = ["email", "sms", "app"];
const SignatureCodeSchema = z.string().regex(/^\d{6}$/, "six digits");
const SignatureRequestParamsSchema = z.strictObject({
  orgId: IdSchema,
  requestId: IdSchema
});
const CreateSignatureRequestSchema = z.strictObject({
  documentId: IdSchema,
  /** The document's current version when absent. */
  versionId: IdSchema.optional(),
  title: z.string().trim().min(1).max(200),
  message: z.string().trim().min(1).max(1e3).optional(),
  level: SignatureLevelSchema.default("simple"),
  signerIds: z.array(IdSchema).min(1).max(10).refine((ids) => new Set(ids).size === ids.length, "each signer once"),
  expiresInDays: z.int().min(1).max(30).default(14)
});
const SignatureListQuerySchema = z.strictObject({
  /** `to_sign`: requests where I am a signer; `sent`: requests I made. */
  box: z.enum(["to_sign", "sent", "all"]).default("all"),
  status: SignatureRequestStatusSchema.optional()
});
const PersonSchema = z.strictObject({ id: IdSchema, name: z.string() });
const SignatureSignerSchema = z.strictObject({
  id: IdSchema,
  user: PersonSchema,
  ordinal: z.int(),
  status: SignerStatusSchema,
  signedAt: IsoDateTimeSchema.nullable(),
  declinedAt: IsoDateTimeSchema.nullable(),
  declineReason: z.string().nullable(),
  /** Qualified level: the certificate the platform signed with. */
  certificate: z.strictObject({ subject: z.string(), fingerprintSha256: z.string() }).nullable()
});
const SignatureRequestSchema = z.strictObject({
  id: IdSchema,
  title: z.string(),
  message: z.string().nullable(),
  level: SignatureLevelSchema,
  status: SignatureRequestStatusSchema,
  document: z.strictObject({
    id: IdSchema,
    name: z.string(),
    versionId: IdSchema,
    /** The version's revision code ("A", "B"…), null when the document has none. */
    revision: z.string().nullable(),
    sha256: z.string()
  }),
  requestedBy: PersonSchema,
  signers: z.array(SignatureSignerSchema),
  expiresAt: IsoDateTimeSchema,
  completedAt: IsoDateTimeSchema.nullable(),
  evidence: z.strictObject({
    status: z.enum(EVIDENCE_STATUSES),
    /** SHA-256 of the canonical evidence record, printed on the certificate. */
    sha256: z.string().nullable()
  }),
  /** What the caller may do. */
  me: z.strictObject({
    isRequester: z.boolean(),
    signerId: IdSchema.nullable(),
    canSign: z.boolean(),
    codeSentAt: IsoDateTimeSchema.nullable()
  }),
  createdAt: IsoDateTimeSchema
});
const SignatureRequestListSchema = z.strictObject({
  items: z.array(SignatureRequestSchema)
});
const SignatureCodeSentSchema = z.strictObject({
  channel: z.enum(SIGNATURE_CODE_CHANNELS),
  sentTo: z.string(),
  expiresAt: IsoDateTimeSchema
});
const SignRequestSchema = z.strictObject({ code: SignatureCodeSchema });
const DeclineSignatureSchema = z.strictObject({ reason: z.string().trim().min(1).max(500) });
const SignatureVerificationSchema = z.strictObject({
  checkedAt: IsoDateTimeSchema,
  documentSha256: z.string(),
  /** The stored file still has the hash fixed when the request was made. */
  documentIntact: z.boolean(),
  /** The evidence record still has its hash; null until the request is completed. */
  evidenceIntact: z.boolean().nullable(),
  /** Qualified signatures checked with their certificate; null for the simple level. */
  signatures: z.array(z.strictObject({ signerId: IdSchema, valid: z.boolean().nullable() }))
});
const SignatureEvidenceUrlSchema = z.strictObject({
  url: z.string(),
  fileName: z.string(),
  expiresAt: IsoDateTimeSchema
});
const PendingEinvoiceSignaturesSchema = z.strictObject({
  count: z.int(),
  items: z.array(z.strictObject({ invoiceId: IdSchema, number: z.string(), attempt: z.int() }))
});
const SigningBatchParamsSchema = z.strictObject({ orgId: IdSchema, batchId: IdSchema });
const SIGNING_BATCH_STATUSES = ["awaiting_code", "signed", "failed", "expired"];
const SigningBatchSchema = z.strictObject({
  id: IdSchema,
  status: z.enum(SIGNING_BATCH_STATUSES),
  invoiceCount: z.int(),
  channel: z.enum(SIGNATURE_CODE_CHANNELS),
  sentTo: z.string(),
  expiresAt: IsoDateTimeSchema
});
const ConfirmSigningBatchSchema = z.strictObject({ code: SignatureCodeSchema });
const SigningBatchResultSchema = z.strictObject({
  id: IdSchema,
  status: z.enum(SIGNING_BATCH_STATUSES),
  signed: z.int()
});
export {
  ConfirmSigningBatchSchema,
  CreateSignatureRequestSchema,
  DeclineSignatureSchema,
  EVIDENCE_STATUSES,
  PendingEinvoiceSignaturesSchema,
  SIGNATURE_CODE_CHANNELS,
  SIGNATURE_LEVELS,
  SIGNATURE_REQUEST_STATUSES,
  SIGNER_STATUSES,
  SIGNING_BATCH_STATUSES,
  SignRequestSchema,
  SignatureCodeSchema,
  SignatureCodeSentSchema,
  SignatureEvidenceUrlSchema,
  SignatureLevelSchema,
  SignatureListQuerySchema,
  SignatureRequestListSchema,
  SignatureRequestParamsSchema,
  SignatureRequestSchema,
  SignatureRequestStatusSchema,
  SignatureSignerSchema,
  SignatureVerificationSchema,
  SignerStatusSchema,
  SigningBatchParamsSchema,
  SigningBatchResultSchema,
  SigningBatchSchema
};
//# sourceMappingURL=index.js.map